import { useSelector } from "react-redux";
import { Alert, Backdrop, Box, Button, Chip, CircularProgress, Paper, Snackbar, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { ConversationMessage, ConversationUserStatus, dateSimplifier, determineImageParty, determineMe } from "../../../redux/conversation/conversationSlice";
import { MessageType, retrieveMessageLatest, RetrieveMessageListRequest, retrieveMessageNewers, retrieveMessageOld, selectMessageList } from "../../../redux/conversation/messageListSlice";
import { LoadState } from "../../../redux/common";
import FragmentCardError from "../../base/FragmentCardError";
import { useEffect, useState } from "react";
import { useAppDispatch } from "../../../app/hooks";
import { MessageSendRequest, messageSendSlice, performMessageSend, selectMessageSend } from "../../../redux/conversation/messageSendSlice";
import { selectedConversationSlice, selectedConversationState} from "../../../redux/navigation/selectedConversationSlice";
import BadgedAvatar from "../../base/BadgedAvatar";
import { MessagesSkeleton } from "./MessagesSkeleton";
import { MessageTile } from "./MessageTile";
import { MessageImageListTile } from "./MessageImageList";

import SendIcon from "@mui/icons-material/Send";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import NotificationsIcon from "@mui/icons-material/Notifications";

import { ElementUploadImages } from "../../base/image/ElementUploadImages";
import { imagesUploadSlice } from "../../../redux/image/imagesUploadSlice";
import { performConversationUpdRead } from "../../../redux/conversation/conversationUpdReadSlice";
import { selectUser } from "../../../redux/user/userSlice";
import { getOrRetrieveNamedObject, selectObjectCache } from "../../../redux/object/objectCacheSlice";
import { ModalPartiesList } from "./ModalPartiesList";
import { changeConversationUserStatus, ChangeConversationUserStatusRequest } from "../../../redux/conversation/conversationUserSlice";
import { selectConversationList } from "../../../redux/conversation/conversationListSlice";
import { filesUploadSlice } from "../../../redux/file/filesUploadSlice";

import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { ElementUploadFiles } from "../../base/file/ElementUploadFiles";
import { MessagePriority } from "../../../redux/conversation/messagePriority";

export interface MessageScreenProps {
    height: number;
    // me: NamedObject;
    // schoolId: string;
}

export const MessagesScreen = (props: MessageScreenProps) => {
    const dispatch = useAppDispatch();

    const selectedConversation = useSelector(selectedConversationState);
    const conversations = useSelector(selectConversationList);
    const messageList = useSelector(selectMessageList);
    const messageSend = useSelector(selectMessageSend);
    const user = useSelector(selectUser);
    const objectCache = useSelector(selectObjectCache);

    const [text, setText] = useState("");
    const [isImage, setIsImage] = useState(false);
    const [isFile, setIsFile] = useState(false);
    const [isPartiesList, setIsPartiesList] = useState(false);

    const conversation = conversations.list?.find((conv) => conv.id == selectedConversation.conversation?.conversationId);
    const imageParty = determineImageParty(conversation);
    const myParty = determineMe(conversation, user.priza.info);
    const me = getOrRetrieveNamedObject(dispatch, objectCache, myParty?.objectTarget, myParty?.targetId);

    useEffect(() => {
        if (!conversation) {
            return;
        }
        if(me?.type != selectedConversation.conversation?.meObjectTarget
            || me?.targetId != selectedConversation.conversation?.meTargetId) {
            dispatch(selectedConversationSlice.actions.clean());
        }
        const request: RetrieveMessageListRequest = {
            conversationId: conversation.id,
        };
        dispatch(retrieveMessageLatest(request));
    }, [conversation]);

    useEffect(() => {
        if (messageSend.state.state == LoadState.Loaded) {
            dispatch(messageSendSlice.actions.clean());
            setText("");
        }
    }, [messageSend.state.state]);

    useEffect(() => {
        if (!conversation) {
            return;
        }
        const convCache = messageList.conversations[conversation.id];
        if (! convCache) {
            return ;
        }
        if (convCache.latest.state == LoadState.Loaded
            && convCache.list
            && convCache.list.length > 0
            && parseInt(convCache.list[0].id) > (conversation.lastReadMessageId ?? 0)
        ) {
            dispatch(performConversationUpdRead({
                conversationId: parseInt(conversation.id),
                messageId: parseInt(convCache.list[0].id),
            }));
        }
    }, [messageSend.state.state, conversation, messageList.conversations]);

    if (!selectedConversation.conversation 
        || !conversation) {
        return <></>;
    }

    const convCache = messageList.conversations[conversation.id];
    if (!convCache || convCache.latest.state == LoadState.Loading) {
        return <MessagesSkeleton height={props.height} />;
    }
    if (convCache.latest.state == LoadState.Failed) {
        return <FragmentCardError error={convCache.latest.error} />;
    }

    const topHeight = 96;
    const messageHeight = props.height - topHeight;
    const avatarSize = 70;
    const subAvatarSize = 37;

    const dismissError = () => {
        dispatch(messageSendSlice.actions.clean());
    };

    const onChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        setText(event.target.value);
    };

    const sendMessage = () => {
        if (!text || text.trim().length == 0) {
            return;
        }
        const request: MessageSendRequest = {
            conversationId: conversation.id,
            partyId: conversation.parties.find((party) => party.objectTarget == me?.type && party.targetId == me.targetId)!.id!,
            text: text,
        };
        dispatch(performMessageSend(request));
    };

    if (isImage) {
        return <Typography variant="body2" sx={{ padding: 2 }}>
            <ElementUploadImages
                back={() => {
                    const request: RetrieveMessageListRequest = {
                        conversationId: conversation.id,
                    };
                    dispatch(retrieveMessageNewers(request));
                    setIsImage(false);
                    dispatch(imagesUploadSlice.actions.clean());
                }}
                target={{ 
                    schoolId: `${me?.schoolId}`, 
                    avatar: false, 
                    conversationId: conversation.id,
                    partyId: conversation.parties.find((party) => party.objectTarget == me?.type && party.targetId == me.targetId)!.id!,
                    objectTarget: imageParty?.objectTarget,
                    targetId: imageParty?.targetId,
                }}
            />
        </Typography>;
    }

    if (isFile) {
        return <Typography variant="body2" sx={{ padding: 2 }}>
            <ElementUploadFiles
                back={() => {
                    const request: RetrieveMessageListRequest = {
                        conversationId: conversation.id,
                    };
                    dispatch(retrieveMessageNewers(request));
                    setIsFile(false);
                    dispatch(filesUploadSlice.actions.clean());
                }}
                target={{ 
                    schoolId: `${me?.schoolId}`, 
                    conversationId: conversation.id,
                    partyId: conversation.parties.find((party) => party.objectTarget == me?.type && party.targetId == me.targetId)!.id!
                }}
            />
        </Typography>;
    }

    const messages = () => {
        const list = (convCache.list || [])
            .filter((msg) => msg.messageType != MessageType.CHANGED)
            ;

        if(list.length == 0) {
            return <Typography variant="body2">Sem mensagens</Typography>;
        }

        const messages: any[] = [];
        let lastDay = "";
        let images: ConversationMessage[] = [];
        for(let index=0; index < list.length; index++) {
            const msg = list[index];
            const thisDay = dateSimplifier(msg.processed);

            if(thisDay != lastDay) {
                images = addMessage( messages, images, 
                    <Box key={thisDay} sx={{textAlign: "center"}}>
                        <Chip label={thisDay} /> 
                    </Box>
                );
            }
            if(msg.messageType == MessageType.IMAGE) {
                if(msg.messagePriority != MessagePriority.Regular) {
                    images = addMessage( messages, images, undefined );
                    images.push(msg);
                    images = addMessage( messages, images, undefined );
                } else if(images.length == 0  || (`${images[0].partyId}` == `${msg.partyId}`)) {
                    images.push(msg);
                } else {
                    images = addMessage( messages, images, undefined );
                }
            } else {
                images = addMessage( messages, images, <MessageTile key={msg.id} conversation={conversation} message={msg} /> );
            }
            lastDay = thisDay;
        }
        images = addMessage( messages, images, undefined );
        return messages;
    };

    const addMessage = (messages: any[], images: ConversationMessage[], message: any) => {
        if(images.length > 0) {
            messages.push(
                <MessageImageListTile key={"images-" + images[0].id} conversation={conversation} messages={images} schoolId={me?.schoolId}/>
            );
        }
        if (message) {
            messages.push(message);
        }
        return [];
    };

    const modalPartiesList = () => {
        if(isPartiesList && selectedConversation.conversation) {
            return <ModalPartiesList
                conversation={conversation}
                close={() => setIsPartiesList(false)}
                title={selectedConversation.conversation.title}
                avatar={selectedConversation.conversation.avatar}
                subtitle={selectedConversation.conversation.subtitle}
                subavatar={selectedConversation.conversation.subavatar}
            />;
        } else {
            return <></>;
        }
    };

    const changeNofication = (status: ConversationUserStatus) => {
        if (!conversation) {
            return;
        }
        const request: ChangeConversationUserStatusRequest = {
            conversationId: conversation.id,
            status: status,
        };
        dispatch(changeConversationUserStatus(request));
    };

    let notification = <></>;
    if(conversation.conversationUserStatus == ConversationUserStatus.SILENT) {
        notification = <Tooltip title="Conversa silenciada (clique para mudar)">
            <div onClick={() => {changeNofication(ConversationUserStatus.ACTIVE);}}>
                <NotificationsOffIcon sx={{ color: "#777", paddingTop: "5px" }} />
            </div>
        </Tooltip>;
    } else if(conversation.conversationUserStatus == ConversationUserStatus.ACTIVE) {
        notification = <Tooltip title="Notificação ativa (clique para silenciar)">
            <div onClick={() => {changeNofication(ConversationUserStatus.SILENT);}}>
                <NotificationsIcon sx={{ color: "#777", paddingTop: "5px" }} />
            </div>
        </Tooltip>;
    }

    return <>
        <Paper elevation={3} sx={{ height: topHeight, borderRadius: 0, borderLeft: "1px black solid" }}>
            <Stack key="messages-screen" direction="row" spacing={0} sx={{ paddingX: 2, paddingY: 0, alignItems: "center" }}>
                <div id="top-bar" onClick={() => setIsPartiesList(true)} >
                    <Box key="avatar" sx={{ width: topHeight, height: topHeight, alignContent: "center", justifyContent: "center", justifyItems: "center" }}>
                        <BadgedAvatar
                            title={selectedConversation.conversation?.title}
                            avatar={selectedConversation.conversation?.avatar}
                            subtitle={selectedConversation.conversation?.subtitle}
                            subavatar={selectedConversation.conversation?.subavatar}
                            size={`${avatarSize}px`}
                            subsize={`${subAvatarSize}px`}
                        />
                    </Box>
                </div>
                <Stack key={"top"} direction="column" spacing={0} sx={{ paddingY: 1, paddingRight: 2, width: "100%" }}>
                    <Stack key="title-container" direction="row" spacing={2}>
                        <Typography key="message-contact-title" variant="h6"
                            sx={{ textWrap: "nowrap", textOverflow: "ellipsis", overflow: "hidden"}}>
                            {selectedConversation.conversation.title}
                        </Typography>
                        <Typography key="message-contact-subtitle" variant="caption" 
                            sx={{ textWrap: "nowrap", textOverflow: "ellipsis", overflow: "hidden", paddingTop: "10px" }}>
                            {selectedConversation.conversation.subtitle}
                        </Typography>
                        {notification}
                    </Stack>
                    <Stack key="send-container" direction="row" spacing={2}>
                        <TextField key="message-text" size='small' id="outlined-basic" label="Mensagem" variant="outlined" fullWidth
                            value={text} onChange={onChangeText}
                            onKeyUp={event => {
                                if (event.key === "Enter") {
                                  sendMessage();
                                }
                            }}/>
                        <Button key="message-send-bt" variant="contained" color="primary" onClick={sendMessage}><SendIcon /></Button>
                        <Button key="image-send-bt" variant="contained" color="primary" 
                            disabled={!imageParty || !imageParty.targetId}
                            onClick={() => {
                                dispatch(imagesUploadSlice.actions.clean());
                                setIsImage(true);
                            }}>
                            <AddPhotoAlternateIcon />
                        </Button>
                        <Button key="file-send-bt" variant="contained" color="primary" 
                            onClick={() => {
                                dispatch(filesUploadSlice.actions.clean());
                                setIsFile(true);
                            }}>
                            <NoteAddIcon />
                        </Button>
                    </Stack>
                </Stack>
            </Stack>
        </Paper>
        <Box sx={{ padding: 2, border: "1px gray solid", backgroundColor: "#EEEEEE", height: messageHeight, overflowY: "scroll" }}>
            {messages()}
            { convCache.moreOld &&
                <Button variant="text" color="primary" onClick={() => {
                    const request: RetrieveMessageListRequest = {
                        conversationId: conversation.id,
                    };
                    dispatch(retrieveMessageOld(request));
                }
                }>Carregar mais mensagens</Button>
            }
        </Box>
        {modalPartiesList()}
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={messageSend.state.state == LoadState.Loading} >
            <CircularProgress color="inherit" />
        </Backdrop>
        <Snackbar open={messageSend.state.state == LoadState.Failed} autoHideDuration={5000} onClose={dismissError}>
            <Alert onClose={dismissError} severity="error" variant="filled" sx={{ width: "100%" }}> Erro ao criar conversa. </Alert>
        </Snackbar>
    </>;

};